import React, { useState } from "react";
import { motion } from "motion/react";
import NoNotifications from "@/assets/icons/NoNotifications.svg";
import useUserData from "@/hooks/useUserData";
import { profilePictures } from "@/lib/data";
import { formatTimestamp } from "@/lib/utils";
import { API_URL } from "@/env";
import { getCookie } from "@/lib/cookies";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import { useNavigate } from "react-router-dom";

const Notifications = () => {
  const { user, setUser } = useUserData();
  const { setCurrentWorkspace, currentWorkspace } = useWorkspaceStore();
  const [invitations, setInvitations] = useState(
    user?.workspaceInvitations || []
  );
  const token = getCookie("token");
  const [loadingInvitations, setLoadingInvitations] = useState<Set<string>>(
    new Set()
  );

  const navigate = useNavigate();

  const { addWorkspace } = useWorkspaceStore();

  const handleInvitation = async (
    invitation: any,
    action: "accept" | "reject"
  ) => {
    try {
      setLoadingInvitations((prev) => new Set(prev).add(invitation._id));

      let response: any;

      // Check if it's a join request or regular invitation
      if (invitation.type === "join_request") {
        // Use the handleJoinRequest endpoint
        response = await fetch(
          `${API_URL}/workspaces/${invitation.workspace._id}/join-requests/${invitation._id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              action: action === "accept" ? "approve" : "reject",
            }),
          }
        );
        const data = await response.json();

        if (data?.workspace) {
          setCurrentWorkspace(data.workspace);
          // navigate(`/dashboard/${data.workspace._id}`);
        }

        const getUser = async () => {
          if (token) {
            const res = await fetch(`${API_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            const user = await res.json();

            // console.log({ user });
            // console.log({ currentWorkspace });

            if (user) {
              setUser(user);
            }
          }
        };

        getUser();
      } else {
        // Use the regular invitation endpoint
        response = await fetch(
          `${API_URL}/workspace-invitations/${invitation._id}/${action}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();

        // console.log({ data });

        if (data?.workspace) {
          setCurrentWorkspace(data.workspace);
          navigate(`/dashboard/${data.workspace._id}`);
        }

        const getUser = async () => {
          if (token) {
            const res = await fetch(`${API_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            const user = await res.json();

            // console.log({ user });

            if (user) {
              setUser(user);
            }
          }
        };

        getUser();
      }

      if (!response.ok) {
        throw new Error(`Failed to ${action} invitation`);
      }

      setInvitations((prev: any) =>
        prev.filter((inv: any) => inv._id !== invitation._id)
      );

      const data = await response.json();

      if (action === "accept" && data?.workspace) {
        addWorkspace(data.workspace);
      }
    } catch (error) {
      console.error(`Error ${action}ing invitation:`, error);
      // Optionally show an error toast/notification here
    } finally {
      setLoadingInvitations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invitation._id);
        return newSet;
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="dropdown-shadow absolute w-[480px] font-medium bottom-[41px] z-[1000] bg-gray-300 left-0 rounded-[16px]"
    >
      <div className="flex text-white h-[48px] p-1 w-full border-b-[1px] border-white/[0.08]">
        <div className="text-[16px] leading-[24px] flex justify-between w-full px-3 transition-all rounded-[12px] ring-0 outline-0 items-center gap-1.5">
          Notifications
          {invitations?.length > 0 && (
            <p className="text-white/[0.32]">{invitations?.length}</p>
          )}
        </div>
      </div>
      {!invitations.length ? (
        <div className="w-full h-[272px] flex-col gap-2 flex items-center justify-center">
          <img src={NoNotifications} />
          <div className="flex flex-col items-center">
            <p className="text-white font-medium text-[16px] leading-[24px]">
              No notifications
            </p>
            <p className="mt-1 w-[240px] text-center text-gray-800 font-medium text-[14px] leading-[20px]">
              No one is looking for you yet, try checking later.
            </p>
          </div>
        </div>
      ) : (
        <div className="w-full max-h-[272px] flex-col gap-2 overflow-scroll no-scrollbar">
          {invitations.map((invitation: any, i: number) => {
            const isLoading = loadingInvitations.has(invitation._id);

            return (
              <div
                key={invitation._id}
                className={`${
                  i !== invitations?.length - 1
                    ? "border-b-[1px] border-white/[0.08]"
                    : ""
                } p-4 flex gap-2 items-start text-[16px] leading-[24px] text-white/[0.32]`}
              >
                <div className="flex gap-4 items-start">
                  <img
                    src={profilePictures[invitation?.sender?.profilePicture]}
                    className="shrink-0 cursor-pointer border-[1px] w-6 h-6 border-white/[0.32] rounded-[50%]"
                  />
                  <div className="">
                    <div className="flex items-center flex-wrap">
                      <div className="mr-1.5">
                        Yo!{" "}
                        <span className="text-white">
                          {invitation?.sender?.email}{" "}
                        </span>
                      </div>
                      <span>
                        {invitation?.type === "join_request"
                          ? "wants to join"
                          : "invited you to"}{" "}
                      </span>
                      <div className="relative inline-flex items-center justify-center mx-1.5">
                        <div
                          style={{
                            color: invitation?.workspace?.color || "#FF0088",
                          }}
                          className="text-[12px] leading-[16px] font-semibold absolute uppercase text-white z-[10]"
                        >
                          {invitation?.workspace?.name[0]}
                        </div>
                        <div
                          style={{
                            background:
                              invitation?.workspace?.color || "#FF0088",
                          }}
                          className="opacity-[0.16] uppercase w-5 h-5 rounded-[6px]"
                        />
                      </div>
                      <span className="text-white mr-1.5">
                        {invitation.workspace.name}
                      </span>
                      workspace
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleInvitation(invitation, "accept")}
                        disabled={isLoading}
                        className="text-[14px] px-4 bg-white hover:bg-white/[0.8] transition-all text-gray-50 h-[36px] flex items-center rounded-[12px] justify-center leading-[24px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleInvitation(invitation, "reject")}
                        disabled={isLoading}
                        className="text-[14px] px-4 bg-white/[0.04] hover:bg-white/[0.08] transition-all h-[36px] flex items-center rounded-[12px] justify-center leading-[24px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-[14px] leading-[24px] shrink-0">
                  {formatTimestamp(invitation.createdAt)}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default Notifications;
